import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export const getProductMovementsList = createAsyncThunk(
    'productMovements/getProductMovementsList',
    async ({ accountId, token, search, start, length, productId, movementType, startDate, endDate }, { rejectWithValue }) => {
    try {
        let url = `${import.meta.env.VITE_API_BASE_URL}/productmovements/list?accountId=${accountId}&start=${start}&length=${length}&draw=1`;
        
        // Aggiungo i parametri di ricerca se presenti
        if (search && search.length > 0) {
            url += `&search[value]=${encodeURIComponent(search)}`;
        } else {
            url += `&search[value]=`;
        }
        
        // Aggiungo filtri opzionali
        if (productId) {
            url += `&productId=${productId}`;
        }
        
        if (movementType) {
            url += `&movementType=${movementType}`;
        }
        
        if (startDate) {
            url += `&startDate=${startDate}`;
        }
        
        if (endDate) {
            url += `&endDate=${endDate}`;
        }
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero dei movimenti di prodotto');
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const getProductMovementDetails = createAsyncThunk(
    'productMovements/getProductMovementDetails',
    async ({ movementId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/productmovements?movementId=${movementId}`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero dei dettagli del movimento');
        }
        
        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const getMovementTypes = createAsyncThunk(
    'productMovements/getMovementTypes',
    async ({ accountId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/productmovements/types?accountId=${accountId}`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero dei tipi di movimento');
        }
        
        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

const productMovementsSlice = createSlice({
    name: 'productMovements',
    initialState: {
        data: null,
        loading: false,
        error: null,
        movementDetails: null,
        loadingDetails: false,
        errorDetails: null,
        movementTypes: [],
        loadingMovementTypes: false,
        errorMovementTypes: null
    },
    reducers: {
        resetMovementDetails: (state) => {
            state.movementDetails = null;
            state.loadingDetails = false;
            state.errorDetails = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // getProductMovementsList
            .addCase(getProductMovementsList.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getProductMovementsList.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(getProductMovementsList.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Errore durante il recupero dei movimenti di prodotto';
            })
            // getProductMovementDetails
            .addCase(getProductMovementDetails.pending, (state) => {
                state.loadingDetails = true;
                state.errorDetails = null;
            })
            .addCase(getProductMovementDetails.fulfilled, (state, action) => {
                state.loadingDetails = false;
                state.movementDetails = action.payload;
            })
            .addCase(getProductMovementDetails.rejected, (state, action) => {
                state.loadingDetails = false;
                state.errorDetails = action.payload || 'Errore durante il recupero dei dettagli del movimento';
            })
            // getMovementTypes
            .addCase(getMovementTypes.pending, (state) => {
                state.loadingMovementTypes = true;
                state.errorMovementTypes = null;
            })
            .addCase(getMovementTypes.fulfilled, (state, action) => {
                state.loadingMovementTypes = false;
                state.movementTypes = action.payload;
            })
            .addCase(getMovementTypes.rejected, (state, action) => {
                state.loadingMovementTypes = false;
                state.errorMovementTypes = action.payload || 'Errore durante il recupero dei tipi di movimento';
            });
    }
});

export const { resetMovementDetails } = productMovementsSlice.actions;

export const selectProductMovementsData = (state) => state.productMovements.data;
export const selectProductMovementsLoading = (state) => state.productMovements.loading;
export const selectProductMovementsError = (state) => state.productMovements.error;
export const selectMovementDetails = (state) => state.productMovements.movementDetails;
export const selectMovementDetailsLoading = (state) => state.productMovements.loadingDetails;
export const selectMovementDetailsError = (state) => state.productMovements.errorDetails;
export const selectMovementTypes = (state) => state.productMovements.movementTypes;
export const selectMovementTypesLoading = (state) => state.productMovements.loadingMovementTypes;
export const selectMovementTypesError = (state) => state.productMovements.errorMovementTypes;

export default productMovementsSlice.reducer;
